import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, photoUrl, fetchCommunityPhotos } from "@/lib/api";
import { RotatingHero } from "@/components/RotatingHero";
import { useTheme } from "@/lib/theme";
import { OSM_TILE_URL, OSM_ATTRIBUTION, tileClassForTheme } from "@/lib/tiles";
import { X, Calendar, MapPin, ArrowRight, Users } from "lucide-react";

const CURATED_RING = "#E87722"; // orange for the site's own rider photos
const COMMUNITY_RING = "#38BDF8"; // sky-blue for community uploads

const buildPhotoIcon = (thumbUrl, ring = CURATED_RING) =>
    L.divIcon({
        html: `<div style="width:56px;height:56px;border-radius:50%;border:2px solid ${ring};overflow:hidden;box-shadow:0 4px 12px rgba(0,0,0,0.5);transition:transform 0.2s"><img src="${thumbUrl}" style="width:100%;height:100%;object-fit:cover" /></div>`,
        className: "photo-marker",
        iconSize: [56, 56],
        iconAnchor: [28, 28],
    });

// Normalize the two photo shapes into one used by this page.
const normalizeCurated = (p) => ({
    ...p,
    source: "curated",
    displayUrl: photoUrl(p.url),
    thumbUrl: `${process.env.REACT_APP_BACKEND_URL}${p.thumb_url}`,
    title: (p.file || "").replace(/\.[^.]+$/, "").replace(/[_-]/g, " "),
});

const normalizeCommunity = (p) => ({
    ...p,
    source: "community",
    displayUrl: `${process.env.REACT_APP_BACKEND_URL}${p.url}`,
    thumbUrl: `${process.env.REACT_APP_BACKEND_URL}${p.url}`,
    file: p.original_filename,
    title: p.caption || "Rider upload",
    dt: p.taken_at || p.created_at,
    alt: null,
});

export default function Journal() {
    const [photos, setPhotos] = useState([]);
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState("all"); // all | curated | community
    const { theme } = useTheme();

    useEffect(() => {
        document.title = "Photo Journal · MotorcycleRoutesIreland";
        Promise.all([
            axios.get(`${API}/photos`).then((r) => (r.data || []).map(normalizeCurated)),
            fetchCommunityPhotos().then((r) => (r.photos || []).map(normalizeCommunity)),
        ])
            .then(([curated, community]) => {
                // Sort community first so the newest rider uploads show at the top of the grid
                setPhotos([...community, ...curated]);
            })
            .catch(() => {});
    }, []);

    const curatedCount = photos.filter((p) => p.source === "curated").length;
    const communityCount = photos.filter((p) => p.source === "community").length;
    const visible = filter === "all" ? photos : photos.filter((p) => p.source === filter);

    return (
        <div data-testid="journal-page">
            <section className="relative pt-40 pb-12 border-b border-[var(--border-hair)] overflow-hidden">
                <RotatingHero
                    images={photos.map((p) => p.displayUrl)}
                    className="absolute inset-0"
                    intervalMs={7000}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#14161A] via-[#14161A]/85 to-[#14161A]/70" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 on-hero">
                    <div className="eyebrow fade-up">Photo Journal</div>
                    <h1 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter fade-up fade-up-delay-1">
                        Every stop,<br /><span className="text-[#E87722]">on the map</span>.
                    </h1>
                    <p className="mt-8 font-body text-[var(--fg-muted)] text-lg max-w-2xl fade-up fade-up-delay-2">
                        Every geotagged photo — our own rider archive plus every public shot uploaded by riders like you.{" "}
                        <Link to="/passport" className="underline hover:text-[#E87722]">Upload yours →</Link>
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.24em] text-[var(--fg-dim)] font-body">
                        <span data-testid="journal-total-count">{photos.length} geotagged photos</span>
                        <span className="opacity-40">·</span>
                        <span className="flex items-center gap-2">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CURATED_RING }} /> {curatedCount} archive
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COMMUNITY_RING }} /> {communityCount} community
                        </span>
                    </div>
                </div>
            </section>

            <section className="py-10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex flex-wrap gap-2 mb-6">
                        {[
                            { key: "all", label: `All (${photos.length})` },
                            { key: "curated", label: `Archive (${curatedCount})` },
                            { key: "community", label: `From Riders (${communityCount})` },
                        ].map((c) => (
                            <button
                                key={c.key}
                                data-testid={`journal-filter-${c.key}`}
                                onClick={() => setFilter(c.key)}
                                className={`px-4 py-2 font-display font-bold uppercase tracking-[0.15em] text-xs border transition-all ${
                                    filter === c.key
                                        ? "bg-[#E87722] text-black border-[#E87722]"
                                        : "text-[var(--fg-muted)] border-[var(--border-hair)] hover:border-[#E87722] hover:text-[#E87722]"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                    <div className="border border-[var(--border-hair)] overflow-hidden" style={{ height: "70vh" }}>
                        <MapContainer center={[53.5, -8]} zoom={7} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }} className={`z-0 ${tileClassForTheme(theme)}`}>
                            <TileLayer
                                url={OSM_TILE_URL}
                                attribution={OSM_ATTRIBUTION}
                            />
                            {visible.map((p, i) => (
                                <Marker
                                    key={`${p.source}-${p.photo_id || p.file || i}`}
                                    position={[p.lat, p.lng]}
                                    icon={buildPhotoIcon(p.thumbUrl, p.source === "community" ? COMMUNITY_RING : CURATED_RING)}
                                    eventHandlers={{ click: () => setSelected(p) }}
                                />
                            ))}
                        </MapContainer>
                    </div>
                </div>
            </section>

            <section className="py-12 border-t border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-6">All Shots</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {visible.map((p, i) => (
                            <button
                                key={`${p.source}-grid-${p.photo_id || p.file || i}`}
                                data-testid={`journal-grid-${i}`}
                                onClick={() => setSelected(p)}
                                className="group relative aspect-square overflow-hidden border border-[var(--border-hair)] hover:border-[#E87722] transition-colors"
                            >
                                <img src={p.thumbUrl} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                {p.source === "community" && (
                                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/70 text-[9px] uppercase tracking-[0.18em] text-white font-display font-bold px-2 py-1">
                                        <Users size={10} /> Rider
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {selected && (
                <div
                    data-testid="journal-lightbox"
                    onClick={() => setSelected(null)}
                    className="fixed inset-0 z-[1000] bg-black/95 backdrop-blur-xl flex items-center justify-center p-6 md:p-12"
                >
                    <button
                        data-testid="journal-lightbox-close"
                        onClick={() => setSelected(null)}
                        className="absolute top-6 right-6 w-12 h-12 border border-white/20 text-[var(--fg)] hover:border-[#E87722] hover:text-[#E87722] flex items-center justify-center"
                    >
                        <X size={20} />
                    </button>
                    <div onClick={(e) => e.stopPropagation()} className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-6 items-center">
                        <img src={selected.displayUrl} alt="" className="w-full max-h-[80vh] object-contain" />
                        <div className="border border-[var(--border-hair)] bg-[var(--card-bg)] p-6">
                            <div className="eyebrow mb-3 flex items-center gap-2">
                                {selected.source === "community" ? (
                                    <><Users size={12} className="text-[#38BDF8]" /> Uploaded by a rider</>
                                ) : (
                                    <>From the road</>
                                )}
                            </div>
                            <div className="font-display font-black uppercase text-[var(--fg)] text-2xl leading-tight tracking-tight mb-6">{selected.title || "Untitled"}</div>
                            {selected.dt && (
                                <div className="flex items-center gap-3 py-3 border-t border-[var(--border-hair)] text-[var(--fg-muted)] text-sm font-body">
                                    <Calendar size={14} className="text-[#E87722]" />{String(selected.dt).split("T")[0].split(" ")[0].replace(/:/g, "-")}
                                </div>
                            )}
                            <div className="flex items-center gap-3 py-3 border-t border-[var(--border-hair)] text-[var(--fg-muted)] text-sm font-body">
                                <MapPin size={14} className="text-[#E87722]" />{Number(selected.lat).toFixed(4)}°, {Number(selected.lng).toFixed(4)}°
                            </div>
                            {selected.alt && (
                                <div className="py-3 border-t border-[var(--border-hair)] text-[var(--fg-muted)] text-sm font-body">
                                    <span className="text-[var(--fg-dim)] uppercase tracking-[0.2em] text-xs">Altitude</span> · {Math.round(selected.alt)} m
                                </div>
                            )}
                            {selected.route_slug && (
                                <Link
                                    to={`/routes/${selected.route_slug}`}
                                    data-testid="lightbox-route-link"
                                    className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-[#E87722] hover:bg-[#F28C38] text-black px-6 py-3 font-display font-bold uppercase tracking-[0.2em] text-xs transition-colors"
                                >
                                    View this route <ArrowRight size={14} />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
